/**
 * TextAction (Workspace v3.x design-system) — the inline text actions that used to
 * lean on the cursor alone (Reassign · + Add collaborator · Edit · Stop repeating ·
 * Reopen). Hover treatment A (Amanda, 2026-06-29): transparent at rest, on hover the
 * pill fills a quiet sage and the text deepens (sage-400 → sage-800), with a short
 * transition. The FILL is the new feedback — cursors stay standard (no custom cursor;
 * `cursor: pointer` comes free with <button>).
 *
 * Use <TextAction> for a fresh button, or `textActionClass` to restyle an existing
 * control in place (e.g. the × on a collaborator chip wants a different rest colour).
 *
 * DestructiveTextAction (design pass 2026-07-25) is the SAME affordance in the
 * pepper register — identical geometry, weight and type scale, so a destructive
 * action never reads as a different KIND of control, only a different tone.
 * It replaces three hand-copied inline class strings (Delete task / Delete
 * intake / Delete card) that had drifted into CloseCompleteBlock.
 *
 * Token note: the rest colour is `pepper-deep`, NOT `pepper`. index.css
 * documents pepper-deep as "small-text danger on paper (6.29:1)", and the
 * hover state is what forces it — text-pepper on the bg-pepper-soft hover fill
 * measures 3.72:1 and fails AA at 12px; pepper-deep on pepper-soft clears at
 * 5.0:1 (and 5.88:1 on cream, 6.29:1 on paper).
 *
 * TextActionOnBand (design pass 2026-07-25, soak r2 punch item 5) is the
 * THIRD register: the same affordance sitting ON a status band (the
 * pepper-soft "Closed" band, the sage-100 "Completed" band). The two
 * Reopen affordances used to be hand-rolled bold-underlined text there —
 * underline is navigation and this is an action (standing law) — and they
 * could not simply take the sage TextAction because sage-400 on those pale
 * washes misses AA. This variant keeps the pill geometry and deepens the
 * rest colour to sage-800 (12:1 on pepper-soft, 10:1 on sage-100), with a
 * tinted-forest hover fill that reads on both bands.
 */

import { type ButtonHTMLAttributes, type ReactNode } from 'react';

const TEXT_ACTION_SHAPE =
  'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-semibold ' +
  'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-sage-400 disabled:opacity-50 disabled:hover:bg-transparent';

export const textActionClass =
  TEXT_ACTION_SHAPE + ' text-sage-400 hover:bg-sage-50 hover:text-sage-800 ' +
  'disabled:hover:text-sage-400';

export const destructiveTextActionClass =
  TEXT_ACTION_SHAPE + ' text-pepper-deep hover:bg-pepper-soft hover:text-pepper-deep ' +
  'disabled:hover:text-pepper-deep';

export const onBandTextActionClass =
  TEXT_ACTION_SHAPE + ' text-sage-800 hover:bg-sage-800/[10%] hover:text-sage-800 ' +
  'disabled:hover:text-sage-800';

export function TextAction({ className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
  return (
    <button type="button" className={textActionClass + (className ? ' ' + className : '')} {...rest}>
      {children}
    </button>
  );
}

export function DestructiveTextAction({ className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
  return (
    <button type="button" className={destructiveTextActionClass + (className ? ' ' + className : '')} {...rest}>
      {children}
    </button>
  );
}

export function TextActionOnBand({ className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
  return (
    <button type="button" className={onBandTextActionClass + (className ? ' ' + className : '')} {...rest}>
      {children}
    </button>
  );
}
